export const integrations = { telegram: 'Telegram', whatsapp: 'WhatsApp', email: 'Email', sendpulse: 'SendPulse', knowledge_ai: 'Knowledge AI' }
export const pageSize = 40

// OpenAPI uses count/next/previous/results; the story also permits pages.
export function normalizePage(data) {
  return { ...data, pages: data.pages ?? Math.max(1, Math.ceil(data.count / pageSize)) }
}

export const views = ['all_operations', 'needs_attention', 'retry_scheduled', 'unknown_outcome']
export const executionStatuses = ['pending', 'running', 'retry_wait', 'succeeded', 'failed', 'unknown', 'blocked', 'cancelled', 'superseded', 'simulated']
export const activeCommand = command => Boolean(command && ['queued', 'running'].includes(command.status))
export const scalar = value => typeof value === 'string' ? value : ''
export const present = value => value !== null && value !== undefined
export const humanize = value => value ? String(value).replace(/_/g, ' ').replace(/^./, char => char.toUpperCase()) : 'Not recorded'
export const tooltip = content => ({ content: String(content || ''), html: false, trigger: 'hover focus', placement: 'top', offset: 8, classes: 'tooltip--new integration-tooltip' })
export const tone = status => ({ succeeded: 'green', applied: 'green', delivered: 'green', read: 'green', failed: 'red', blocked: 'red', pending: 'blue', running: 'blue', accepted: 'blue', queued: 'blue', retry_wait: 'amber', unknown: 'amber' }[status] || 'neutral')

export function compactId(value, edgeLength = 3) {
  if (value === null || value === undefined) return '—'
  const id = String(value)
  if (id.length <= (edgeLength * 2) + 3) return id
  return `${id.slice(0, edgeLength)}...${id.slice(-edgeLength)}`
}

export function dateLabel(value, exact = false) {
  if (!value || !Number.isFinite(Date.parse(value))) return '—'
  const result = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Dubai',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(exact ? { second: '2-digit' } : {}),
    hour12: false
  }).format(new Date(value))
  return exact ? `${result} · Asia/Dubai (UTC+04:00)` : result
}

export function periodQuery(period) {
  const [from, to] = period || []
  return {
    created_from: from ? new Date(`${from}T00:00:00+04:00`).toISOString() : undefined,
    created_to: to ? new Date(Date.parse(`${to}T00:00:00+04:00`) + 86400000).toISOString() : undefined
  }
}

export function queryPeriod(query) {
  return ['created_from', 'created_to'].map((key, index) => {
    const value = Date.parse(scalar(query[key]))
    return Number.isFinite(value) ? new Date(value + 14400000 - (index ? 1 : 0)).toISOString().slice(0, 10) : null
  })
}

export function cleanQuery(query) {
  return Object.keys(query).reduce((result, key) => {
    if (query[key] !== '' && query[key] !== null && query[key] !== undefined) result[key] = query[key]
    return result
  }, {})
}

const operationFilterKeys = ['search', 'operation_type', 'provider', 'source', 'account', 'sender', 'created_from', 'created_to']
const logFilterKeys = ['search', 'result', 'method', 'account', 'sender', 'created_from', 'created_to']

export function listParams(query, integration, operations, summary = false) {
  const keys = operations ? operationFilterKeys : logFilterKeys
  const params = operations ? { integration } : {}
  keys.forEach(key => { if (query[key]) params[key] = query[key] })
  if (!summary) {
    params.page = query.page || 1
    params.page_size = pageSize
    if (operations) params.view = query.view || 'all_operations'
  }
  return params
}

export function queryError(query, operations) {
  const allowed = operations ? [...operationFilterKeys, 'page', 'page_size', 'view', 'operation'] : [...logFilterKeys, 'page', 'page_size', 'log']
  if (Object.keys(query).some(key => !allowed.includes(key) || Array.isArray(query[key]))) return 'Unsupported or repeated URL parameter. Clear filters to reset the view.'
  if (query.page && !/^[1-9]\d*$/.test(query.page)) return 'Page must be a positive integer.'
  if (query.page_size && query.page_size !== '40') return 'Page size must be 40.'
  if (query.search && query.search.length > 255) return 'Search is limited to 255 characters.'
  if (operations && query.view && !views.includes(query.view)) return 'Unknown operation view.'
  if (['created_from', 'created_to'].some(key => query[key] && !Number.isFinite(Date.parse(query[key])))) return 'Invalid period in URL.'
  if (query.created_from && query.created_to && Date.parse(query.created_from) >= Date.parse(query.created_to)) return 'The start of the period must be before its end.'
  return ''
}

export function errorMessage(error, entity = 'Record') {
  const status = error?.response?.status
  if (status === 403) return 'Access denied. You do not have permission to view this data.'
  if (status === 404) return `${entity} not found or unavailable.`
  if (status === 400) return error.response.data?.details || 'The request is no longer valid. Refresh and try again.'
  return 'Unable to load data. Check your connection and try again.'
}

export function recipientLabel(recipient) {
  if (!recipient) return '—'
  if (recipient.count > 1) return `${recipient.count} recipients`
  if (recipient.type === 'unknown') return recipient.address || '—'
  return recipient.label || recipient.address || '—'
}

export function templateLabel(template) {
  if (!template) return '—'
  return template.name || template.slug || (template.provider_id ? `Provider ID: ${template.provider_id}` : template.local_id ? `Local ID: ${template.local_id}` : '—')
}

function normalizeMessageText(value) {
  return value.replace(/\u00a0/g, ' ').replace(/[ \t]+\n/g, '\n').replace(/\n[ \t]+/g, '\n').replace(/\n{3,}/g, '\n\n')
}

export function messageParts(value, breakLabels = false) {
  if (typeof value !== 'string') return []
  if (!/<\/?[a-z][^>]*>|&(?:#\d+|#x[\da-f]+|[a-z]+);/i.test(value)) return [{ text: value }]
  // Parse in an inert fragment; only text and validated link data leave it.
  const template = document.createElement('template')
  template.innerHTML = value
  template.content.querySelectorAll('script,style,iframe,object,embed,svg,math,template,noscript').forEach(node => node.remove())
  template.content.querySelectorAll('br').forEach(node => node.replaceWith('\n'))
  if (breakLabels) template.content.querySelectorAll('b,strong').forEach(node => {
    if (/:\s*$/.test(node.textContent)) node.prepend('\n')
  })
  if (breakLabels) template.content.querySelectorAll('a[href]').forEach(node => node.before('\n'))
  template.content.querySelectorAll('p,div,li,ul,ol,blockquote,pre,h1,h2,h3,h4,h5,h6,tr').forEach(node => {
    node.prepend('\n')
    node.append('\n')
  })
  template.content.querySelectorAll('td,th').forEach(node => node.append(' '))
  const parts = []
  const append = (text, href) => {
    if (!text) return
    const previous = parts[parts.length - 1]
    if (!href && previous && !previous.href) previous.text += text
    else parts.push({ text, ...(href ? { href } : {}) })
  }
  const visit = node => {
    if (node.nodeType === 3) { append(node.textContent); return }
    if (node.nodeName === 'A') {
      const href = (node.getAttribute('href') || '').trim()
      if (/^https?:\/\//i.test(href)) {
        try {
          const url = new URL(href)
          if (['http:', 'https:'].includes(url.protocol)) { append(node.textContent || href, href); return }
        } catch (error) { /* Invalid links remain plain text. */ }
      }
    }
    Array.from(node.childNodes).forEach(visit)
  }
  visit(template.content)
  parts.forEach(part => { part.text = normalizeMessageText(part.text) })
  if (parts.length) {
    parts[0].text = parts[0].text.trimStart()
    parts[parts.length - 1].text = parts[parts.length - 1].text.trimEnd()
  }
  return parts
}

export function messageText(value, breakLabels = false) {
  if (typeof value !== 'string') return value
  return messageParts(value, breakLabels).map(part => part.href && part.text.trim() !== part.href ? `${part.text} (${part.href})` : part.text).join('')
}

export function newRequestId() {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 15) | 64
  bytes[8] = (bytes[8] & 63) | 128
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function relatedRoute(router, object, groups, canAccess = () => false) {
  const navigation = object?.navigation
  if (!navigation?.route_name) return null
  const route = router.getRoutes().find(item => item.name === navigation.route_name)
  if (!route) return null
  if (route.meta.access) {
    if (!canAccess(route.meta.access)) return null
  } else if (route.meta.roles?.length && !groups.some(group => route.meta.roles.includes(group.code))) return null
  return { name: route.name, params: navigation.params || {}, query: navigation.query || {} }
}

export function safeHtml(html) {
  // Template contents are inert even during parsing: no image/frame requests
  // can start before sanitization. Never attach this fragment to the app DOM.
  const template = document.createElement('template')
  template.innerHTML = html
  template.content.querySelectorAll('script,iframe,frame,object,embed,form,input,button,textarea,select,link,meta,base,svg,math,audio,video,source,template,noscript').forEach(node => node.remove())
  template.content.querySelectorAll('*').forEach(node => {
    Array.from(node.attributes).forEach(attribute => {
      if (!['style', 'class', 'width', 'height', 'colspan', 'rowspan', 'alt'].includes(attribute.name)) node.removeAttribute(attribute.name)
    })
  })
  return `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; form-action 'none'; base-uri 'none'"></head><body>${template.innerHTML}</body></html>`
}
